import { useQuery } from '@tanstack/react-query';
import { Alert, Button, Progress, Space, Tag, Typography } from 'antd';
import { useNavigate } from 'react-router';
import type { IngestionRun } from '@titularis/api-client';
import { api } from '../api';

/**
 * Acompanhamento do Cadastro Assistido numa prova.
 *
 * **Não há botão de acionar, e a ausência é a decisão de 29/08/2026.** O
 * cadastro passou a ser feito pelo Claude Code, pelo MCP, na máquina de quem
 * opera: quem escolhe a prova e os campos é a ferramenta `abrir_prova`, e um
 * botão aqui enfileiraria para um worker que não existe mais.
 *
 * O que a tela faz é o que ela sempre precisou fazer e não fazia sozinha: contar
 * o que aconteceu. Etapas, custo, motivo da parada e o caminho para revisar as
 * sugestões - tudo isso é lido por quem revisa, não por quem executa.
 */
const ROTULO_DA_ETAPA: Record<string, string> = {
  extraction: 'Extração',
  answer_key: 'Gabarito',
  classification: 'Classificação',
};

/** Enquanto o run corre, a tela se atualiza sozinha; parado, não insiste. */
const intervaloDeAtualizacao = (run: IngestionRun | undefined) =>
  run && run.status === 'running' ? 3000 : false;

export function CadastroAssistido({
  examId,
  temProva,
  temGabarito,
}: {
  examId: string;
  temProva: boolean;
  temGabarito: boolean;
}) {
  const navigate = useNavigate();

  const runs = useQuery({
    queryKey: ['ingestion-runs', examId],
    queryFn: () => api.listIngestionRuns(examId),
  });

  const runAtualId = runs.data?.[0]?.id;

  const run = useQuery({
    queryKey: ['ingestion-run', runAtualId],
    queryFn: () => api.getIngestionRun(runAtualId!),
    enabled: Boolean(runAtualId),
    refetchInterval: (consulta) => intervaloDeAtualizacao(consulta.state.data),
  });

  const dados = run.data;
  const faltaArquivo = !temProva || !temGabarito;

  return (
    <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
      <Typography.Text type="secondary">
        A transcrição da prova é feita pelo Claude Code, pelo MCP. Nada é publicado: tudo entra
        como sugestão, para revisão.
      </Typography.Text>

      {faltaArquivo ? (
        <Alert
          type="warning"
          showIcon
          title="Faltam PDFs."
          description="O Cadastro Assistido precisa do PDF da prova e do gabarito (ou padrão de resposta) para poder ser aberto."
        />
      ) : null}

      {dados ? (
        <SituacaoDoRun
          run={dados}
          onVerSugestoes={() => void navigate(`/questoes?examId=${examId}&revisao=1`)}
        />
      ) : (
        <Typography.Text type="secondary">
          Esta prova ainda não foi cadastrada por nenhum run.
        </Typography.Text>
      )}
    </Space>
  );
}

function SituacaoDoRun({
  run,
  onVerSugestoes,
}: {
  run: IngestionRun;
  onVerSugestoes: () => void;
}) {
  const concluidas = run.steps.filter((etapa) => etapa.status === 'succeeded').length;

  return (
    <Space orientation="vertical" size={8} style={{ width: '100%' }}>
      <Space size={8} wrap>
        <Typography.Text strong>Último processamento</Typography.Text>
        <Tag color={corDoStatus(run.status)}>{rotuloDoStatus(run.status)}</Tag>
        {/* Custo à vista, durante e depois: é dinheiro por chamada. */}
        <Tag>US$ {run.costUsd.toFixed(4)}</Tag>
      </Space>

      {run.status === 'running' ? (
        /*
         * O total é o das etapas que já existem, e não um número esperado: quem
         * decide quantas há é a sessão que está trabalhando, e prever três
         * faria a barra encolher quando a classificação não fosse pedida.
         */
        <Progress
          percent={run.steps.length > 0 ? Math.round((concluidas / run.steps.length) * 100) : 0}
          size="small"
          status="active"
        />
      ) : null}

      <Space orientation="vertical" size={2} style={{ width: '100%' }}>
        {run.steps.map((etapa) => (
          <Typography.Text key={etapa.id} type="secondary" style={{ fontSize: 12 }}>
            {ROTULO_DA_ETAPA[etapa.step] ?? etapa.step}: {rotuloDoStatus(etapa.status)}
            {etapa.model ? ` · ${etapa.model}` : ''}
            {Number(etapa.costUsd) > 0 ? ` · US$ ${Number(etapa.costUsd).toFixed(4)}` : ''}
          </Typography.Text>
        ))}
      </Space>

      {run.status === 'running' ? (
        <Alert
          type="info"
          showIcon
          title="Em andamento"
          description="Uma sessão do Claude Code está trabalhando nesta prova. As etapas acima vão se completando conforme ela grava."
        />
      ) : null}

      {run.status === 'failed' ? (
        <Alert type="error" showIcon title="O cadastro não terminou" description={run.errorMessage} />
      ) : null}

      {run.status === 'succeeded' ? (
        <Alert
          type="success"
          showIcon
          title="Sugestões prontas para revisão"
          description="Nada foi publicado: as questões entraram como sugestão."
          action={
            <Button size="small" onClick={onVerSugestoes}>
              Revisar
            </Button>
          }
        />
      ) : null}
    </Space>
  );
}

const rotuloDoStatus = (status: string): string =>
  ({
    queued: 'na fila',
    running: 'em andamento',
    needs_confirmation: 'aguardando confirmação',
    succeeded: 'concluído',
    failed: 'falhou',
    pending: 'pendente',
    skipped: 'pulada',
  })[status] ?? status;

const corDoStatus = (status: string): string =>
  ({
    queued: 'default',
    running: 'processing',
    needs_confirmation: 'warning',
    succeeded: 'green',
    failed: 'red',
  })[status] ?? 'default';
